// AdminList.js
import { useState, useEffect } from "react";
import { getAdminsList, deleteAdmin } from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";
import "./AdminList.css";

// MUI Icons
import DeleteIcon from "@mui/icons-material/Delete";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import { IconButton } from "@mui/material";

const AdminList = () => {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [pagination, setPagination] = useState({
    page: 1,
    totalCount: 0,
    pageSize: 10,
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    adminId: null,
    adminName: "",
  });

  const fetchAdmins = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getAdminsList({ page, limit: 10, sortBy, sortOrder });
      setAdmins(res.data.admins || []);
      setPagination({
        page: res.data.page || 1,
        totalCount: res.data.totalCount || 0,
        pageSize: res.data.pageSize || 10,
      });
    } catch (err) {
      console.error(err);
      alert("Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins(1);
  }, [sortBy, sortOrder]);

  const handleDelete = async () => {
    try {
      await deleteAdmin({ adminId: deleteModal.adminId });
      alert("Admin deleted successfully");
      fetchAdmins(pagination.page);
    } catch (err) {
      alert(err.response?.data?.message || "Delete failed");
    } finally {
      setDeleteModal({ open: false, adminId: null, adminName: "" });
    }
  };

  // Helper to get role badge color
  const getRoleColor = (role) => {
    const map = {
      Superadmin: "role-super",
      Admin: "role-admin",
      Manager: "role-manager",
      "Support Agent": "role-support",
      Finance: "role-finance",
      Operations: "role-ops",
      "Read-Only": "role-readonly",
    };
    return map[role] || "";
  };

  return (
    <div className="admin-list-page">
      {loading ? (
        <Loader />
      ) : (
        <div className="table-wrapper">
          <ListControls sortOptions={[{value:"createdAt",label:"Created date"},{value:"fullName",label:"Name"},{value:"email",label:"Email"},{value:"role",label:"Role"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
          <table className="data_table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>2FA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.length === 0 ? (
                <tr>
                  <td colSpan="6">No admins found.</td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id}>
                    <td>
                      <strong>{admin.fullName}</strong>
                    </td>
                    <td>{admin.email}</td>
                    <td>{admin.mobilenumber}</td>
                    <td>
                      <span
                        className={`role-badge ${getRoleColor(admin.role)}`}
                      >
                        {admin.role}
                      </span>
                    </td>
                    <td>
                      {admin.isTwoFactorEnabled ? " Enabled" : " Disabled"}
                    </td>
                    <td>
                      <IconButton
                        disabled={admin.role === "Superadmin"}
                        onClick={() =>
                          setDeleteModal({
                            open: true,
                            adminId: admin.id,
                            adminName: admin.fullName,
                          })
                        }
                      >
                        <DeleteIcon
                          sx={{
                            color:
                              admin.role === "Superadmin"
                                ? "gray"
                                : "var(--red)",
                            fontSize: "28px",
                          }}
                        />{" "}
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination pagedata={pagination} onPageChange={fetchAdmins} />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() =>
          setDeleteModal({ open: false, adminId: null, adminName: "" })
        }
        title="Delete Admin"
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{deleteModal.adminName}</strong>? This action cannot be
          undone.
        </p>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() =>
              setDeleteModal({ open: false, adminId: null, adminName: "" })
            }
          >
            Cancel
          </button>
          <button className="btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminList;
